import {_} from 'lodash';
import * as ko from "knockout";
import {IRouteDefinitionParams, IRuleset, TBaseOrObservableType, TDialogOperation} from "../../index";
import {ObjectTypeId} from "../../constants";
import {TabManager} from "../tab-list/TabManager";
import {getRouteDefinition} from "../../constants/RouteDefinitions";
import {nsRuleset} from "../ruleset/nsRuleset";
import {RequestRouteData} from "../../req/RequestRouteData";
import {IMutableRulesetParams} from "../ruleset/mutable/mutableRuleset";
import {global, lm} from "../../appContext";
import pubsub from "../../pubsub";
import {ICreateRulesetEventParams, IUpdateRulesetEventParams} from "../ruleset/index";
import {nsTooltip} from "../../util/nsTooltip";
import {IComboboxV2Options} from "./v2";
import {convertToArray, getAnyValue, getNextId} from "../../util/misc";
import rulesetHtml from './ruleset.html?raw';

export const template = rulesetHtml;

export interface IComboboxOptions_Ruleset {
    comboboxOptions?: Partial<IComboboxV2Options<IRuleset>>
    objectTypeIds?: TBaseOrObservableType<ObjectTypeId | ObjectTypeId[]>
    canChangeObjectType?: TBaseOrObservableType<boolean>
    showIcon?: boolean
}

export class viewModel {
    /** Merged options object shared with v2 (reload / isReady written onto this). */
    private readonly v2Options: IComboboxV2Options<IRuleset>
    private readonly selected: KnockoutObservable<IRuleset | null>
    private readonly objectTypeIdsSubscription?: KnockoutSubscription;
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    private readonly showIcon: boolean
    private eventId: string = 'combobox-ruleset-event-' + getNextId()
    /** Unique CSS anchor so each ⋮ menu can use fixed positioning without colliding. */
    private readonly functionsAnchorName = `--combobox-ruleset-fn-${getNextId()}`
    private readonly functionsAnchorStyle = {'anchor-name': this.functionsAnchorName}
    private readonly functionsMenuStyle = {'position-anchor': this.functionsAnchorName}

    constructor(private params: IComboboxOptions_Ruleset) {
        this.showIcon = params.showIcon === true
        const callerOptions = params.comboboxOptions ?? {}
        callerOptions.selected = callerOptions.selected ?? ko.observable<IRuleset | null>(null)
        this.selected = callerOptions.selected
        const defaults: IComboboxV2Options<IRuleset> = {
            prepareData: {
                filter: o => this.getObjectTypeIds().includes(o.objecttype?.id),
                sort: arr => _.sortBy(arr, (o: IRuleset) => o.name.trim().toLowerCase()),
            },
            inputAttrs: {placeholder: lm.t('none selected')},
            isReady: this.isReady,
            option: {
                label: o => o.name,
                tooltip: o => o ? lm.t(nsTooltip.getTitle_ruleset(o) ?? '') : '',
                key: o => o.id,
                sortBy: [],
            },
            promise: () => nsRuleset.getAll(),
            selected: this.selected,
        };
        this.v2Options = _.defaultsDeep(callerOptions, defaults) as IComboboxV2Options<IRuleset>
        if (this.v2Options.data != null) {
            delete this.v2Options.promise
        }

        // reload is assigned when v2 mounts. Events before that are a no-op (reload?.) —
        // acceptable; the child's own initial fetch covers startup.
        if (params.objectTypeIds && ko.isSubscribable(params.objectTypeIds)) {
            this.objectTypeIdsSubscription = params.objectTypeIds.subscribe(() => {
                this.v2Options.reload?.()
            });
        }

        pubsub.on(this,'ruleset.updated', (e: JQuery.Event, params: IUpdateRulesetEventParams) => {
            if (this.eventId == params.eventId) {
                this.selected(params.newRuleset)
            }
            this.v2Options.reload?.()
        })

        pubsub.on(this,'ruleset.created', (e: JQuery.Event, params: ICreateRulesetEventParams) => {
            if (this.eventId == params.eventId) {
                this.selected(params.newRuleset)
            }
            this.v2Options.reload?.()
        })
    }

    private getCreateParams(): IRouteDefinitionParams {
        const routeDefParams: IMutableRulesetParams = {
            __dialog: {
                callbackFn: (ruleset: IRuleset) => {
                    nsRuleset.invalidateCachesAfterRulesetMutation()
                    const createEventParams: ICreateRulesetEventParams = {eventId: this.eventId, newRuleset: ruleset};
                    pubsub.trigger('ruleset.created', createEventParams)
                    this.selected(ruleset)
                },
                width: 'auto',
            },
            __routeDef: getRouteDefinition('createruleset', {
                showHeadingButtons: false,
                isDialog: true
            })!,
            canChangeObjectType: this.params.canChangeObjectType ?? true,
            objectTypeId: this.getObjectTypeIds(),
        }
        routeDefParams.__tabManager = new TabManager(routeDefParams)
        return routeDefParams
    }

    private getReadParams(): IRouteDefinitionParams {
        const selected = this.selected()
        const routeDefParams: IRouteDefinitionParams = {
            __dialog: {
                width: 'auto',
            },
            __id: selected!.id,
            __objectTypeId: this.getObjectTypeId(),
            __requestRouteData: new RequestRouteData(getRouteDefinition('rulesetdetails')!, {id: selected!.id}),
            __routeDef: getRouteDefinition('rulesetdetails', {
                showHeadingButtons: false,
                showPageButtons: false,
                isDialog: true
            })!,
            __routeParams: {id: selected!.id},
        }
        routeDefParams.__tabManager = new TabManager(routeDefParams)
        return routeDefParams
    }

    private getUpdateParams(): IRouteDefinitionParams {
        const selected = this.selected()
        const routeDefParams: IMutableRulesetParams = {
            __dialog: {
                callbackFn: (ruleset: IRuleset) => {
                    nsRuleset.invalidateCachesAfterRulesetMutation()
                    const updateEventParams: IUpdateRulesetEventParams = {
                        eventId: this.eventId,
                        oldRuleset: selected!,
                        newRuleset: ruleset
                    };
                    pubsub.trigger('ruleset.updated', updateEventParams)
                },
                width: 'auto',
            },
            __id: selected!.id,
            __objectTypeId: this.getObjectTypeId(),
            __requestRouteData: new RequestRouteData(getRouteDefinition('editruleset')!, {id: selected!.id}),
            __routeDef: getRouteDefinition('editruleset', {showHeadingButtons: false, isDialog: true})!,
            __routeParams: {id: selected!.id},
            canChangeObjectType: false,
            objectTypeId: this.getObjectTypeId(),
        }
        routeDefParams.__tabManager = new TabManager(routeDefParams)
        return routeDefParams
    }

    private getRouteDefinition = getRouteDefinition

    private getObjectTypeIds = ko.pureComputed((): ObjectTypeId[] => {
        const objectTypeIds = getAnyValue(this.params.objectTypeIds)
        if (!objectTypeIds) {
            return [ObjectTypeId.User, ObjectTypeId.Department, ObjectTypeId.Group, ObjectTypeId.FormData]
        }
        return convertToArray(objectTypeIds);
    })

    private getObjectTypeId = ko.pureComputed((): ObjectTypeId => {
        return this.getObjectTypeIds()[0] ?? ObjectTypeId.User
    })

    private showDialog(operation: TDialogOperation) {
        switch (operation) {
            case "create":
                global.dialogManager.addCustom({
                    name: 'component/ruleset/mutable/create',
                    params: this.getCreateParams(),
                })
                break;
            case "read":
                global.dialogManager.addCustom({
                    name: 'component/ruleset/read/details',
                    params: this.getReadParams(),
                })
                break;
            case "update":
                global.dialogManager.addCustom({
                    name: 'component/ruleset/mutable/edit',
                    params: this.getUpdateParams(),
                })
                break;
            case "delete":
                break;
            case "preview":
                nsRuleset.fnPreviewRuleset(this.selected()!)
                break;
        }
    }

    dispose() {
        pubsub.off(this)
        this.objectTypeIdsSubscription?.dispose()
    }
}
